"use client";

import { useState } from "react";
import { PillGroup, SelectField } from "@/components/tool-kit/primitives";
import { GibbsEquilibriumPanel } from "@/components/tools/chem-physical/gibbs-equilibrium-panel";
import { GibbsPanel } from "@/components/tools/chem-physical/gibbs-panel";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";

/** English strings. A translated content file overrides these through `ui`. */
export const GIBBS_FREE_ENERGY_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to calculate?",
  modeGibbs: "ΔG from ΔH and ΔS",
  modeEquilibrium: "ΔG° and the equilibrium constant",
  sigFigs: "Significant figures in the answer",
  sigMatch: "Match my data",
  sigChoice:
    "one: {count} significant figure|other: {count} significant figures",
  solveLabel: "Solve for",
  results: "Results",
  working: "The working",
  answerSuffix: "{label} (answer)",
  tryLabel: "Try an example",
  exampleVaporization: "Water vaporizing at 298.15 K",
  exampleMelting: "Ice melting at 263.15 K",
  termG: "Gibbs free energy (ΔG)",
  termH: "Enthalpy change (ΔH)",
  termS: "Entropy change (ΔS)",
  termT: "Temperature (T)",
  unitEnergyG: "ΔG unit",
  unitEnergyH: "ΔH unit",
  unitEntropy: "ΔS unit",
  unitTemperature: "Temperature unit",
  entropyHint:
    "Entropy is usually in J/(mol·K) while enthalpy is in kJ/mol. The units are converted for you.",
  spontaneityAtT: "At this temperature",
  spontaneous: "Spontaneous (ΔG < 0)",
  nonSpontaneous: "Not spontaneous (ΔG > 0)",
  atEquilibrium: "At equilibrium (ΔG = 0)",
  crossover: "Crossover temperature (ΔH ÷ ΔS)",
  noCrossover: "None",
  patternAllT:
    "ΔH is negative and ΔS is positive, so ΔG is negative at every temperature: the process is spontaneous at all temperatures.",
  patternLowT:
    "ΔH and ΔS are both negative: the process is spontaneous below the crossover temperature and not spontaneous above it.",
  patternHighT:
    "ΔH and ΔS are both positive: the process is spontaneous above the crossover temperature and not spontaneous below it.",
  patternNever:
    "ΔH is positive and ΔS is negative, so ΔG is positive at every temperature: the process is not spontaneous at any temperature.",
  patternZero: "ΔH and ΔS are both zero, so ΔG is zero at every temperature.",
  equation: "ΔG = ΔH − TΔS",
  rearrangedG: "ΔG = ΔH − TΔS",
  rearrangedH: "ΔH = ΔG + TΔS",
  rearrangedS: "ΔS = (ΔH − ΔG) ÷ T",
  rearrangedT: "T = (ΔH − ΔG) ÷ ΔS",
  convertLine: "{label}: {value} {unit} = {converted}",
  substitutedG: "ΔG = {h} − {t} × {s} = {result} {unit}",
  substitutedH: "ΔH = {g} + {t} × {s} = {result} {unit}",
  substitutedS: "ΔS = ({h} − {g}) ÷ {t} = {result} {unit}",
  substitutedT: "T = ({h} − {g}) ÷ {s} = {result} {unit}",
  answerLine: "{result} = {answer}",
  crossoverLine: "Crossover: ΔG = 0 when T = ΔH ÷ ΔS = {h} ÷ {s} = {result}",
  kIfStandard:
    "If ΔH and ΔS are standard values (ΔH°, ΔS°), then ΔG° = ΔG and the equilibrium constant is K = {k} (log₁₀ K = {log}) at this temperature.",
  plotLabel: "Graph of ΔG against temperature",
  plotX: "Temperature (K)",
  plotY: "ΔG (kJ/mol)",
  plotCrossover: "Crossover",
  plotNow: "Your T",
  modeFindK: "Find K from ΔG°",
  modeFindDg0: "Find ΔG° from K",
  modeFindDg: "Find ΔG from ΔG° and Q",
  standardDeltaG: "Standard free energy change (ΔG°)",
  deltaGNow: "Free energy change (ΔG)",
  equilibriumConstant: "Equilibrium constant (K)",
  reactionQuotient: "Reaction quotient (Q)",
  log10K: "log₁₀ K",
  reactionDirection: "Direction",
  kHint: "Dimensionless and positive. E-notation works: 1.74e-10.",
  qHint: "Dimensionless and positive, built from the current activities.",
  dirForward: "Forward (Q < K)",
  dirReverse: "Reverse (Q > K)",
  dirEquilibrium: "At equilibrium (Q = K)",
  meaningLarge:
    "K is greater than 1 (ΔG° is negative): products are favored at equilibrium.",
  meaningSmall:
    "K is less than 1 (ΔG° is positive): reactants are favored at equilibrium. K equal to 1 corresponds to ΔG° = 0.",
  eqK: "K = e^(−ΔG° ÷ RT)",
  eqKRt: "RT = 8.314462618 J/(mol·K) × T = {rt} J/mol",
  eqKLn: "ln K = −ΔG° ÷ RT = −({dg0} J/mol) ÷ {rt} J/mol = {lnk}",
  eqKResult: "K = e^({lnk}) = {k}  (log₁₀ K = {log})",
  eqDg0: "ΔG° = −RT ln K",
  eqDg0Result: "ΔG° = −{rt} J/mol × ln({k}) = {dg0} J/mol",
  eqDg: "ΔG = ΔG° + RT ln Q",
  eqDgResult: "ΔG = {dg0} J/mol + {rt} J/mol × ln({q}) = {dg} J/mol",
  eqDgK: "For comparison, K = {k}; the reaction runs forward while Q < K.",
  rNote: "R = 8.314462618 J/(mol·K) (exact, NIST).",
  standardNote:
    "Standard state: 1 bar for gases (IUPAC), 1 mol/L for solutes. K is dimensionless and uses activities, so ΔG° and K refer to the temperature you enter.",
  enterValues: "Enter the remaining values to see the answer.",
  errorUnreadable:
    "{label} is not a number. Use digits, such as 2.50 or 1.5e3.",
  errorNonPositive: "{label} must be greater than zero.",
  errorBelowZero:
    "{label} is at or below absolute zero (0 K, −273.15 °C, −459.67 °F), which is impossible.",
  errorZeroEntropy:
    "ΔS is zero, so ΔG does not depend on temperature and no temperature can be found. Enter a non-zero ΔS.",
  errorResultBelowZero:
    "No positive temperature fits these values: ΔH − ΔG and ΔS must have the same sign. Check the signs.",
  copy: "Copy answer",
  copied: "Copied!",
  copyText: "{label}: {value}",
} as const;

type Mode = "gibbs" | "equilibrium";
const SIG_CHOICES = ["3", "4", "5", "6"] as const;

export default function GibbsFreeEnergyCalculatorWidget() {
  const strings = useToolUi(GIBBS_FREE_ENERGY_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("gibbs");
  const [sigChoice, setSigChoice] = useState("match");
  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="gfe-mode"
        onChange={setMode}
        options={[
          { value: "gibbs", label: strings.modeGibbs },
          { value: "equilibrium", label: strings.modeEquilibrium },
        ]}
        value={mode}
      />
      <div className="max-w-xs">
        <SelectField
          id="gfe-sig"
          label={strings.sigFigs}
          onChange={setSigChoice}
          options={[
            { value: "match", label: strings.sigMatch },
            ...SIG_CHOICES.map((value) => ({
              value,
              label: tPlural(strings.sigChoice, locale, Number(value), {
                count: fmt(Number(value)),
              }).trim(),
            })),
          ]}
          value={sigChoice}
        />
      </div>
      {mode === "gibbs" ? (
        <GibbsPanel sigChoice={sigChoice} strings={strings} />
      ) : (
        <GibbsEquilibriumPanel sigChoice={sigChoice} strings={strings} />
      )}
    </>
  );
}
